/**
 * Web push on this device (Employee rebuild B24).
 *
 * The backend has had VAPID routes for months (routers/notifications.py:
 * /push/vapid-public-key, /subscribe, /unsubscribe) and nothing in the app
 * ever called them, so no device was ever subscribed and every push the
 * server tried reached nobody.
 *
 * The subscription is made through the worker the app already registers
 * (services/swUpdate.ts, vite-plugin-pwa's /sw.js), whose push handlers are
 * public/push-handlers.js. In development there is no worker; `ready` never
 * settles there, so every wait on it is bounded.
 *
 * The endpoint is remembered in localStorage so SIGN-OUT, which is
 * synchronous, can name it to the server (authStore.logout) — a device handed
 * back must stop receiving the previous person's reminders.
 */
import { apiGet, apiPost } from '@/utils/api';

const ENDPOINT_KEY = 'spaciora-push-endpoint';
const READY_TIMEOUT_MS = 4000;

export type PushState =
  | 'unsupported'      // no Notification / PushManager / worker in this browser
  | 'not-configured'   // the deployment has no VAPID key
  | 'denied'           // the person blocked notifications for this site
  | 'subscribed'       // this device is subscribed
  | 'off';             // could be turned on

export function pushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'PushManager' in window
    && typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator;
}

function rememberEndpoint(endpoint: string | null): void {
  try {
    if (endpoint) localStorage.setItem(ENDPOINT_KEY, endpoint);
    else localStorage.removeItem(ENDPOINT_KEY);
  } catch { /* storage blocked: sign-out simply cannot name the device */ }
}

/** The endpoint this device last subscribed with, synchronously. */
export function rememberedEndpoint(): string | null {
  try {
    return localStorage.getItem(ENDPOINT_KEY);
  } catch {
    return null;
  }
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return null;
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), READY_TIMEOUT_MS));
  return Promise.race([navigator.serviceWorker.ready, timeout]);
}

/** The VAPID application server key, as the bytes PushManager wants. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

async function vapidKey(): Promise<string | null> {
  const r = await apiGet<{ vapid_public_key: string; configured: boolean }>(
    '/notifications/push/vapid-public-key',
  );
  return r?.configured && r.vapid_public_key ? r.vapid_public_key : null;
}

/** Where this device stands, without asking the person anything. */
export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (!(await vapidKey())) return 'not-configured';
  const reg = await registration();
  if (!reg) return 'unsupported';
  const sub = await reg.pushManager.getSubscription();
  return sub ? 'subscribed' : 'off';
}

/**
 * Ask for permission, subscribe through the worker, register with the server.
 * Resolves to the resulting state; never throws for a refusal — a person
 * saying no is an answer, not an error.
 */
export async function turnOnPush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const key = await vapidKey();
  if (!key) return 'not-configured';
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';
  const reg = await registration();
  if (!reg) return 'unsupported';
  const sub = (await reg.pushManager.getSubscription())
    ?? (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
    }));
  const json = sub.toJSON();
  await apiPost('/notifications/subscribe', {
    endpoint: sub.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
    user_agent: navigator.userAgent.slice(0, 500),
  });
  rememberEndpoint(sub.endpoint);
  return 'subscribed';
}

/** Stop push on this device: tell the server, then drop the browser's copy. */
export async function turnOffPush(): Promise<void> {
  const reg = await registration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  const endpoint = sub?.endpoint ?? rememberedEndpoint();
  if (endpoint) await apiPost('/notifications/unsubscribe', { endpoint });
  if (sub) await sub.unsubscribe();
  rememberEndpoint(null);
}

/**
 * Sign-out's half. The server side rides on POST /auth/logout (the refresh
 * token is the authority there, so this works with an expired access token);
 * this drops the browser's own subscription so nothing more is delivered here
 * even if that request never arrived. Fire-and-forget: sign-out cannot wait.
 */
export function forgetPushOnSignOut(): void {
  rememberEndpoint(null);
  void registration()
    .then((reg) => reg?.pushManager.getSubscription())
    .then((sub) => sub?.unsubscribe())
    .catch(() => { /* the server has already removed it; nothing else to do */ });
}
